import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  BarChart2,
  Calendar,
  CheckCircle,
  ChevronDown,
  Clock,
  Cpu,
  ExternalLink,
  FileText,
  Globe,
  Image,
  Info,
  Maximize2,
  Monitor,
  RefreshCw,
  Smartphone,
  XCircle,
  Zap
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import websiteService from "../../services/websiteService";
import websitePageSpeedService from "../../services/WebsitePageSpeedService";
import PageSpeedAnalysisModal from "./components/PageSpeedAnalysisModal";
import Toast from "../../components/Toast";
import WebsitePages from "./components/WebsitePages";

// Calculate performance score based on Lighthouse methodology
// Weights from https://developer.chrome.com/docs/lighthouse/performance/performance-scoring/
const calculatePerformanceScore = (metrics) => {
  if (!metrics || !metrics.first_paint || !metrics.load_time) return null;
  
  // Define metric weights as per Lighthouse
  const weights = {
    FCP: 0.10,  // First Contentful Paint
    LCP: 0.25,  // Largest Contentful Paint (using first_paint as a proxy)
    TBT: 0.30,  // Total Blocking Time
    CLS: 0.15,  // Cumulative Layout Shift (assuming 0)
    SI: 0.10,   // Speed Index
    TTI: 0.10   // Time to Interactive (using load_time as a proxy)
  };
  
  // Calculate the scores for each metric using the Lighthouse scoring approach
  const fcpScore = calculateFCPScore(metrics.first_paint);
  const lcpScore = calculateLCPScore(metrics.first_paint); // Using first_paint as proxy for LCP
  const tbtScore = calculateTBTScore(metrics.cpu_load_percent); // Using CPU load as a proxy for TBT
  const clsScore = 1.0; // Assuming CLS is great (1.0)
  const siScore = calculateSIScore(metrics.load_time);
  const ttiScore = calculateTTIScore(metrics.load_time);
  
  // Calculate weighted average
  const weightedScore = 
    weights.FCP * fcpScore +
    weights.LCP * lcpScore +
    weights.TBT * tbtScore +
    weights.CLS * clsScore +
    weights.SI * siScore +
    weights.TTI * ttiScore;
  
  // Return a score between 0-100
  return Math.round(weightedScore * 100);
};

// Scoring functions based on Lighthouse curves
const calculateFCPScore = (fcp) => {
  // FCP scoring: 0-1800ms (good), 1800-3000ms (needs improvement), >3000ms (poor)
  if (fcp <= 1800) return 1.0;
  if (fcp <= 3000) return 0.9 - (fcp - 1800) / 12000;
  return Math.max(0, 0.9 - (fcp - 3000) / 6000);
};

const calculateLCPScore = (lcp) => {
  // LCP scoring: 0-2500ms (good), 2500-4000ms (needs improvement), >4000ms (poor)
  if (lcp <= 2500) return 1.0;
  if (lcp <= 4000) return 0.9 - (lcp - 2500) / 15000;
  return Math.max(0, 0.9 - (lcp - 4000) / 10000);
};

const calculateTBTScore = (cpu) => {
  // Using CPU load as proxy for TBT: <30% (good), 30-50% (needs improvement), >50% (poor)
  if (cpu <= 30) return 1.0;
  if (cpu <= 50) return 0.9 - (cpu - 30) / 200;
  return Math.max(0, 0.9 - (cpu - 50) / 100);
};

const calculateSIScore = (loadTime) => {
  // Speed Index scoring approximation based on load time
  if (loadTime <= 3000) return 1.0;
  if (loadTime <= 5000) return 0.9 - (loadTime - 3000) / 20000;
  return Math.max(0, 0.9 - (loadTime - 5000) / 15000);
};

const calculateTTIScore = (loadTime) => {
  // TTI scoring approximation based on load time
  if (loadTime <= 3500) return 1.0;
  if (loadTime <= 5500) return 0.9 - (loadTime - 3500) / 20000;
  return Math.max(0, 0.9 - (loadTime - 5500) / 15000);
};

const WebsiteDetailPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  
  // State
  const [website, setWebsite] = useState(null);
  const [pageSpeedDesktop, setPageSpeedDesktop] = useState(null);
  const [pageSpeedMobile, setPageSpeedMobile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("desktop"); // 'desktop' or 'mobile'
  const [toast, setToast] = useState({ open: false, message: "", severity: "" });
  const [expandedMetrics, setExpandedMetrics] = useState(true); // Default expanded
  const [expandedOpportunities, setExpandedOpportunities] = useState(true);
  const [performanceScores, setPerformanceScores] = useState({
    desktop: 0,
    mobile: 0
  });
  
  // PageSpeed analysis state
  const [pageSpeedAnalysis, setPageSpeedAnalysis] = useState({
    isRunning: false,
    progress: 0,
    status: 'starting',
    message: 'Initializing...'
  });
  
  // Define fetchData outside the useEffect, so it can be used elsewhere in the component
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Fetch website data
      const websiteResponse = await websiteService.getWebsiteById(id);
      const websiteData = websiteResponse.website || websiteResponse;
      
      setWebsite(websiteData);
      
      // Fetch desktop PageSpeed data using the website URL and ID
      try {
        const siteUrl = websiteData?.root_url || '';
        
        const desktopResponse = await websitePageSpeedService.getPageSpeedByUrlAndWebsiteId(
          siteUrl, 
          id,
          { 
            is_mobile: false,
            limit: 1
          }
        );

        // Process desktop data if available
        if (desktopResponse.pageSpeeds && Array.isArray(desktopResponse.pageSpeeds) && desktopResponse.pageSpeeds.length > 0) {
          const analysis = desktopResponse.pageSpeeds[0];
          try {
            // Parse raw metrics and data
            const desktopRawMetrics = JSON.parse(analysis.raw_metrics);
            let desktopRawData = null;
            
            try {
              if (analysis.raw_data) {
                const parsedData = JSON.parse(analysis.raw_data);
                desktopRawData = parsedData.raw_data 
                  ? JSON.parse(parsedData.raw_data) 
                  : parsedData;
              }
            } catch (dataParseError) {
              console.error("Error parsing desktop raw_data:", dataParseError);
            }
            
            // Store the parsed metrics directly on the analysis object for easier access
            analysis.parsedRawMetrics = desktopRawMetrics;
            analysis.parsedRawData = desktopRawData;
            
            // Set desktop data
            setPageSpeedDesktop(analysis);
            
            // Calculate performance score using our algorithm
            const score = calculatePerformanceScore(desktopRawMetrics);
            
            setPerformanceScores(prev => ({
              ...prev,
              desktop: score !== null ? score : 0
            }));
          } catch (parseError) {
            console.error("Error parsing desktop metrics:", parseError);
            setPageSpeedDesktop(analysis);
          }
        } else if (desktopResponse.pageSpeeds && desktopResponse.pageSpeeds.length > 0) {
          // Fallback to pageSpeeds if it exists (for backward compatibility)
          setPageSpeedDesktop(desktopResponse.pageSpeeds[0]);
          
          try {
            const metrics = JSON.parse(desktopResponse.pageSpeeds[0].raw_metrics);
            const score = calculatePerformanceScore(metrics);
            setPerformanceScores(prev => ({
              ...prev,
              desktop: score !== null ? score : 0
            }));
          } catch (parseError) {
            console.error("Error parsing desktop fallback metrics:", parseError);
          }
        }
      } catch (desktopError) {
        console.error("Error fetching desktop PageSpeed data:", desktopError);
      }
      
      // Fetch mobile PageSpeed data using the website URL and ID
      try {
        const siteUrl = websiteData?.root_url || '';
        console.log(`Fetching mobile PageSpeed data for URL: ${siteUrl}, website ID: ${id}`);
        
        const mobileResponse = await websitePageSpeedService.getPageSpeedByUrlAndWebsiteId(
          siteUrl, 
          id,
          { 
            is_mobile: true,
            limit: 1
          }
        );

        // Process mobile data if available
        if (mobileResponse.pageSpeeds && Array.isArray(mobileResponse.pageSpeeds) && mobileResponse.pageSpeeds.length > 0) {
          const analysis = mobileResponse.pageSpeeds[0];
          try {
            // Parse raw metrics and data
            const mobileRawMetrics = JSON.parse(analysis.raw_metrics);
            let mobileRawData = null;
            
            try {
              if (analysis.raw_data) {
                const parsedData = JSON.parse(analysis.raw_data);
                mobileRawData = parsedData.raw_data 
                  ? JSON.parse(parsedData.raw_data) 
                  : parsedData;
              }
            } catch (dataParseError) {
              console.error("Error parsing mobile raw_data:", dataParseError);
            }
            
            // Store the parsed metrics directly on the analysis object for easier access
            analysis.parsedRawMetrics = mobileRawMetrics;
            analysis.parsedRawData = mobileRawData;
            
            // Set mobile data
            setPageSpeedMobile(analysis);
            
            // Calculate performance score using our algorithm
            const score = calculatePerformanceScore(mobileRawMetrics);
            setPerformanceScores(prev => ({
              ...prev,
              mobile: score !== null ? score : 0
            }));
          } catch (parseError) {
            console.error("Error parsing mobile metrics:", parseError);
            setPageSpeedMobile(analysis);
          }
        } else if (mobileResponse.pageSpeeds && mobileResponse.pageSpeeds.length > 0) {
          // Fallback to pageSpeeds if it exists (for backward compatibility)
          setPageSpeedMobile(mobileResponse.pageSpeeds[0]);
          
          try {
            const metrics = JSON.parse(mobileResponse.pageSpeeds[0].raw_metrics);
            const score = calculatePerformanceScore(metrics);
            setPerformanceScores(prev => ({
              ...prev,
              mobile: score !== null ? score : 0
            }));
          } catch (parseError) {
            console.error("Error parsing mobile fallback metrics:", parseError);
          }
        }
      } catch (mobileError) {
        console.error("Error fetching mobile PageSpeed data:", mobileError);
      }
    } catch (error) {
      console.error("Error fetching website data:", error);
      setError("Failed to load website details. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [id]); // Remove website?.id from dependencies as it's not needed

  // Fetch website and PageSpeed data on component mount
  useEffect(() => {
    if (!user || !id) return;
    fetchData();
  }, [id, user, fetchData]); // Add fetchData to dependency array
  
  // Run analysis for a specific page or the main website
  const runPageAnalysis = async (input) => {
    // Check if input is provided and has a URL
    if (!input || !input.url) {
      console.error("Cannot run PageSpeed analysis: Invalid data", input);
      setToast({
        open: true,
        message: "Cannot run analysis: Missing URL information",
        severity: "error"
      });
      return;
    }
    
    try {
      // Set analysis state with better initial message
      setPageSpeedAnalysis({
        isRunning: true,
        progress: 0,
        status: 'starting',
        message: `Starting PageSpeed analysis for ${input.url}`
      });
      
      // Determine whether this is a website or page object
      const isWebsiteObject = !input.website_id && input.id;
      
      if (isWebsiteObject) {
        // This is the main website - use analyzeWebsite
        await websitePageSpeedService.analyzeWebsite(input, {
          mobile: true,
          desktop: true,
          onProgress: (progress) => {
            // Update progress state
            setPageSpeedAnalysis({
              isRunning: progress.status !== 'complete' && progress.status !== 'error',
              progress: progress.progress,
              status: progress.status,
              message: progress.message || `Processing ${input.url} (${progress.progress}% complete)`
            });
          }
        });
      } else {
        // This is a page - use analyzePage
        await websitePageSpeedService.analyzePage(input, {
          desktop: true,
          mobile: true,
          onProgress: (progress) => {
            // Update progress state
            setPageSpeedAnalysis({
              isRunning: progress.status !== 'complete' && progress.status !== 'error',
              progress: progress.progress,
              status: progress.status,
              message: progress.message || `Processing ${input.url} (${progress.progress}% complete)`
            });
          }
        });
      }
      
      // When analysis completes, refresh data before showing success
      await fetchData();
      
      // Show success toast
      setToast({
        open: true,
        message: `Analysis for ${input.url} completed successfully!`,
        severity: "success"
      });
    } catch (error) {
      console.error("Error analyzing page:", error);
      
      // Update progress with error
      setPageSpeedAnalysis({
        isRunning: false,
        status: 'error',
        message: 'Error analyzing page',
        progress: 0
      });
      
      // Show user-friendly error message instead of raw error
      let errorMessage = "We encountered a problem while analyzing this page.";
      
      if (error.message) {
        if (error.message.includes("timeout") || error.message.includes("timed out")) {
          errorMessage = "Analysis timed out. The page may be too large or the server is experiencing high load.";
        } else if (error.message.includes("network") || error.message.includes("connection")) {
          errorMessage = "Network error: Unable to connect to the page. Please check if the URL is accessible.";
        } else if (error.message.includes("404") || error.message.includes("not found")) {
          errorMessage = "The page was not found. Please verify that the URL is correct.";
        } else if (error.message.includes("403") || error.message.includes("forbidden")) {
          errorMessage = "Access to the page is forbidden. The website may be blocking our analysis tools.";
        } else if (error.message.includes("500") || error.message.includes("server error")) {
          errorMessage = "The website returned a server error. Please try again later.";
        }
      }
      
      // Show detailed error toast notification
      setToast({
        open: true,
        message: errorMessage,
        severity: "error"
      });
    }
  };

  // Close toast notification
  const closeToast = () => {
    setToast({ ...toast, open: false });
  };

  // Set up showToast function for child components
  const showToast = (message, severity = "info") => {
    setToast({
      open: true,
      message,
      severity
    });
  };

  // Format date using the user's local date and time format
  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    
    try {
      // Create a date object
      const date = new Date(dateString);
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return "Invalid date";
      }
      
      // Format the date using the browser's locale settings
      return date.toLocaleString(undefined, {
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Date error";
    }
  };

  const formatMetric = (value, unit) => {
    if (value === undefined || value === null) {
      return "N/A";
    }
    
    if (unit === "s") {
      // Convert to seconds with one decimal place
      return `${(value / 1000).toFixed(1)}s`;
    } else if (unit === "ms") {
      // Round to nearest millisecond
      return `${Math.round(value)}ms`;
    } else {
      // Return the raw value with two decimal places for things like CLS
      return value.toFixed(2);
    }
  };

  // LCP ratings
  const getLCPScoreColor = (lcp) => {
    // LCP is in milliseconds
    if (lcp <= 2500) return "bg-green-500"; // Good
    if (lcp <= 4000) return "bg-yellow-500"; // Needs improvement
    return "bg-red-500"; // Poor
  };

  const getLCPRating = (lcp) => {
    if (lcp <= 2500) return "Good";
    if (lcp <= 4000) return "Needs improvement";
    return "Poor";
  };

  // CLS ratings
  const getCLSScoreColor = (cls) => {
    if (cls <= 0.1) return "bg-green-500"; // Good
    if (cls <= 0.25) return "bg-yellow-500"; // Needs improvement
    return "bg-red-500"; // Poor
  };

  const getCLSRating = (cls) => {
    if (cls <= 0.1) return "Good";
    if (cls <= 0.25) return "Needs improvement";
    return "Poor";
  };

  // TBT ratings (as a proxy for FID)
  const getTBTScoreColor = (tbt) => {
    if (tbt <= 200) return "bg-green-500"; // Good
    if (tbt <= 600) return "bg-yellow-500"; // Needs improvement
    return "bg-red-500"; // Poor
  };

  const getTBTRating = (tbt) => {
    if (tbt <= 200) return "Good";
    if (tbt <= 600) return "Needs improvement";
    return "Poor";
  };

  // Speed Index color
  const getSpeedIndexColor = (speedIndex) => {
    // Speed Index is in milliseconds
    if (speedIndex <= 3400) return "bg-green-500"; // Fast
    if (speedIndex <= 5800) return "bg-yellow-500"; // Moderate
    return "bg-red-500"; // Slow
  };

  // Server response color
  const getServerResponseColor = (responseTime) => {
    if (responseTime <= 200) return "bg-green-500 h-2 rounded-full";
    if (responseTime <= 600) return "bg-yellow-500 h-2 rounded-full";
    return "bg-red-500 h-2 rounded-full";
  };

  const getServerResponseClass = (responseTime) => {
    if (responseTime <= 200) return "bg-green-100 text-green-800";
    if (responseTime <= 600) return "bg-yellow-100 text-yellow-800";
    return "bg-red-100 text-red-800";
  };

  const getServerResponseText = (responseTime) => {
    if (responseTime <= 200) return "Fast";
    if (responseTime <= 600) return "Average";
    return "Slow";
  };

  // JavaScript execution time color
  const getBootupTimeColor = (bootupTime) => {
    if (bootupTime <= 600) return "bg-green-500 h-2 rounded-full";
    if (bootupTime <= 1200) return "bg-yellow-500 h-2 rounded-full";
    return "bg-red-500 h-2 rounded-full";
  };

  // Format bytes to human readable format
  const formatBytes = (bytes, decimals = 1) => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  // Helper to create circular gauge for scores
  const renderCircularGauge = (score, size = 'large', label = '') => {
    const normalizedScore = Math.round((score || 0) * 100);
    const radius = size === 'large' ? 40 : 24;
    const strokeWidth = size === 'large' ? 8 : 4;
    const circumference = 2 * Math.PI * radius;
    const strokeDasharray = circumference;
    const strokeDashoffset = circumference - (normalizedScore / 100) * circumference;
    
    // Determine color based on score
    let color = "#ef4444"; // red-500
    if (normalizedScore >= 90) {
      color = "#22c55e"; // green-500
    } else if (normalizedScore >= 50) {
      color = "#eab308"; // yellow-500
    }
    
    return (
      <div className={`relative flex flex-col items-center justify-center ${size === 'large' ? 'h-28 w-28' : 'h-16 w-16'}`}>
        <svg className="absolute" width={radius * 2 + strokeWidth} height={radius * 2 + strokeWidth}>
          <circle
            className="text-gray-200"
            strokeWidth={strokeWidth}
            stroke="currentColor"
            fill="transparent"
            r={radius}
            cx={radius + strokeWidth/2}
            cy={radius + strokeWidth/2}
          />
          <circle
            className="transition-all duration-500 ease-in-out"
            strokeWidth={strokeWidth}
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            stroke={color}
            fill="transparent"
            r={radius}
            cx={radius + strokeWidth/2}
            cy={radius + strokeWidth/2}
            transform={`rotate(-90 ${radius + strokeWidth/2} ${radius + strokeWidth/2})`}
          />
        </svg>
        <div className="flex flex-col items-center justify-center z-10">
          <span className={`${size === 'large' ? 'text-3xl font-bold' : 'text-xl font-bold'}`}>{normalizedScore}</span>
        </div>
      </div>
    );
  };

  // Render opportunity card for improvement suggestions
  const renderOpportunityCard = (title, description, score, learnMoreUrl) => {
    let icon = null;
    let cardClass = "";
    
    if (score >= 0.9) {
      icon = <CheckCircle className="h-5 w-5 text-green-500" />;
      cardClass = "border-green-100 bg-green-50";
    } else if (score >= 0.5) {
      icon = <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      cardClass = "border-yellow-100 bg-yellow-50";
    } else {
      icon = <XCircle className="h-5 w-5 text-red-500" />;
      cardClass = "border-red-100 bg-red-50";
    }
    
    return (
      <div className={`p-4 border rounded-md ${cardClass}`}>
        <div className="flex items-center gap-3">
          {icon}
          <h3 className="font-medium">{title}</h3>
        </div>
        <p className="mt-2 text-sm text-gray-600">{description}</p>
        <div className="mt-3 flex justify-end">
          <a 
            href={learnMoreUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:underline"
          >
            Learn more
          </a>
        </div>
      </div>
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="container p-4 mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <div className="inline-flex items-center gap-2">
            <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-gray-600">Loading website details...</span>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="container p-4 mx-auto">
        <div className="bg-red-100 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-6">
          <p>{error}</p>
          <Link to="/dashboard/websites" className="text-blue-600 hover:underline mt-2 inline-block">
            &larr; Back to websites
          </Link>
        </div>
      </div>
    );
  }

  // Website not found
  if (!website) {
    return (
      <div className="container p-4 mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold mb-4">Website Not Found</h2>
          <p className="text-gray-600 mb-4">The requested website could not be found.</p>
          <Link to="/dashboard/websites" className="text-blue-600 hover:underline">
            &larr; Back to websites
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container p-4 mx-auto">
      {/* Toast notification */}
      {toast.open && (
        <Toast
          open={toast.open}
          message={toast.message}
          severity={toast.severity}
          onClose={closeToast}
        />
      )}
      
      {/* PageSpeed Analysis Modal */}
      <PageSpeedAnalysisModal
        isOpen={pageSpeedAnalysis.isRunning}
        progress={pageSpeedAnalysis.progress}
        status={pageSpeedAnalysis.status}
        message={pageSpeedAnalysis.message}
      />
      
      {/* Back button */}
      <div className="mb-6">
        <Link to="/dashboard/websites" className="text-blue-600 hover:underline flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" />
          Back to websites
        </Link>
      </div>
      
      {/* Website details and performance scores side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Website Details */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h1 className="text-2xl font-bold mb-4 flex items-center">
            <Globe className="h-6 w-6 mr-2 text-blue-600" />
            {website?.name}
          </h1>
          
          <div className="border-b pb-4 mb-4">
            <a 
              href={website?.root_url} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-blue-600 hover:underline flex items-center gap-1 mt-1"
            >
              <ExternalLink className="h-4 w-4" />
              {website?.root_url}
            </a>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-start gap-2">
              <FileText className="h-5 w-5 text-gray-500 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-gray-500">Description</h3>
                <p className="text-gray-900">{website?.description || "No description available"}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-2">
              <Calendar className="h-5 w-5 text-gray-500 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-gray-500">Date Added</h3>
                <p className="text-gray-900">{formatDate(website?.created_at)}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-2">
              <Activity className="h-5 w-5 text-gray-500 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-gray-500">Last PageSpeed Analysis</h3>
                <p className="text-gray-900">
                  {pageSpeedDesktop?.created_at ? formatDate(pageSpeedDesktop.created_at) : "Never analyzed"}
                </p>
              </div>
            </div>
          </div>
          
          <div className="mt-6">
            <button 
              onClick={() => runPageAnalysis({...website, url: website.root_url})} 
              disabled={pageSpeedAnalysis.isRunning}
              className="inline-flex items-center gap-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`h-4 w-4 ${pageSpeedAnalysis.isRunning ? "animate-spin" : ""}`} />
              {pageSpeedAnalysis.isRunning ? "Running Analysis..." : "Run PageSpeed Analysis"}
            </button>
          </div>
        </div>
        
        {/* Performance Score Gauges */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-bold mb-4 flex items-center justify-center">
            <BarChart2 className="h-5 w-5 mr-2 text-blue-600" />
            Performance Scores
          </h2>
          
          {/* Score Gauges */}
          {(pageSpeedDesktop || pageSpeedMobile) ? (
            <div className="flex flex-col items-center">
              {/* Main performance score with improved styling */}
              <div className="flex flex-col items-center mb-8">
                <div className="flex flex-col items-center">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Performance</p>
                  {renderCircularGauge(
                    performanceScores[activeTab] / 100,
                    'large'
                  )}
                </div>
                <div className="mt-3 text-center">
                  <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${
                    performanceScores[activeTab] >= 90
                      ? "bg-green-100 text-green-800"
                      : performanceScores[activeTab] >= 50
                      ? "bg-yellow-100 text-yellow-800"
                      : "bg-red-100 text-red-800"
                  }`}>
                    {performanceScores[activeTab] >= 90 ? "Fast" : 
                     performanceScores[activeTab] >= 50 ? "Moderate" : "Slow"}
                  </span>
                </div>
              </div>
              
              {/* Other scores with improved layout */}
              <div className="grid grid-cols-3 gap-8 w-full max-w-md mx-auto">
                <div className="flex flex-col items-center">
                  <p className="text-xs font-medium text-gray-600 mb-1">Accessibility</p>
                  {renderCircularGauge(
                    (activeTab === "desktop" ? 
                      pageSpeedDesktop?.parsedRawData?.categories?.accessibility?.score || 0 :
                      pageSpeedMobile?.parsedRawData?.categories?.accessibility?.score || 0),
                    'small'
                  )}
                </div>
                <div className="flex flex-col items-center">
                  <p className="text-xs font-medium text-gray-600 mb-1">Best Practices</p>
                  {renderCircularGauge(
                    (activeTab === "desktop" ? 
                      pageSpeedDesktop?.parsedRawData?.categories?.["best-practices"]?.score || 0 :
                      pageSpeedMobile?.parsedRawData?.categories?.["best-practices"]?.score || 0),
                    'small'
                  )}
                </div>
                <div className="flex flex-col items-center">
                  <p className="text-xs font-medium text-gray-600 mb-1">SEO</p>
                  {renderCircularGauge(
                    (activeTab === "desktop" ? 
                      pageSpeedDesktop?.parsedRawData?.categories?.seo?.score || 0 :
                      pageSpeedMobile?.parsedRawData?.categories?.seo?.score || 0),
                    'small'
                  )}
                </div>
              </div>
              
              <div className="mt-6 text-sm text-gray-500 text-center">
                <Clock className="h-4 w-4 inline mr-1" />
                Last analyzed: {formatDate((activeTab === "desktop" ? 
                  pageSpeedDesktop?.created_at : 
                  pageSpeedMobile?.created_at))}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Zap className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 mb-4">No PageSpeed data available.</p>
              <button
                onClick={() => runPageAnalysis({...website, url: website.root_url})}
                disabled={pageSpeedAnalysis.isRunning}
                className="inline-flex items-center gap-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`h-4 w-4 ${pageSpeedAnalysis.isRunning ? "animate-spin" : ""}`} />
                Run Analysis
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Device type toggle moved outside for better accessibility */}
      {(pageSpeedDesktop || pageSpeedMobile) && (
        <div className="mb-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <h3 className="text-lg font-semibold flex items-center">
                <Cpu className="h-5 w-5 mr-2 text-blue-600" />
                PageSpeed Insights Results
              </h3>
              
              <div className="flex items-center bg-gray-100 rounded-lg p-1 ml-0 sm:ml-4">
                <button
                  onClick={() => setActiveTab("desktop")}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm transition-colors ${
                    activeTab === "desktop"
                      ? "bg-white shadow-sm text-blue-600 font-medium"
                      : "text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  <Monitor className="h-4 w-4" />
                  Desktop
                </button>
                <button
                  onClick={() => setActiveTab("mobile")}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm transition-colors ${
                    activeTab === "mobile"
                      ? "bg-white shadow-sm text-blue-600 font-medium"
                      : "text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  <Smartphone className="h-4 w-4" />
                  Mobile
                </button>
              </div>
              
              <div className="ml-auto text-xs text-gray-500 hidden sm:block">
                Switch between device types to view different results
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* PageSpeed Insights Section with better UI/UX */}
      {(activeTab === "desktop" ? pageSpeedDesktop : pageSpeedMobile) && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-bold flex items-center">
              <Cpu className="h-5 w-5 mr-2 text-blue-600" />
              PageSpeed Insights - {activeTab === "desktop" ? "Desktop" : "Mobile"}
            </h2>
          </div>
          
          {/* Core Web Vitals - Always visible */}
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-md font-bold mb-4">Core Web Vitals</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* LCP */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <h4 className="text-sm font-medium">Largest Contentful Paint</h4>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    getLCPRating((activeTab === "desktop" ? 
                      pageSpeedDesktop?.parsedRawMetrics?.audits?.["largest-contentful-paint"]?.numericValue || 4000 :
                      pageSpeedMobile?.parsedRawMetrics?.audits?.["largest-contentful-paint"]?.numericValue || 4500)) === "Good" 
                      ? "bg-green-100 text-green-800" 
                      : getLCPRating((activeTab === "desktop" ? 
                        pageSpeedDesktop?.parsedRawMetrics?.audits?.["largest-contentful-paint"]?.numericValue || 4000 :
                        pageSpeedMobile?.parsedRawMetrics?.audits?.["largest-contentful-paint"]?.numericValue || 4500)) === "Needs improvement"
                        ? "bg-yellow-100 text-yellow-800" 
                        : "bg-red-100 text-red-800"
                  }`}>
                    {getLCPRating((activeTab === "desktop" ? 
                      pageSpeedDesktop?.parsedRawMetrics?.audits?.["largest-contentful-paint"]?.numericValue || 4000 :
                      pageSpeedMobile?.parsedRawMetrics?.audits?.["largest-contentful-paint"]?.numericValue || 4500))}
                  </span>
                </div>
                <p className="text-2xl font-bold mt-2">
                  {formatMetric((activeTab === "desktop" ? 
                    pageSpeedDesktop?.parsedRawMetrics?.audits?.["largest-contentful-paint"]?.numericValue || 4000 :
                    pageSpeedMobile?.parsedRawMetrics?.audits?.["largest-contentful-paint"]?.numericValue || 4500), "s")}
                </p>
                <div className="mt-2">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${getLCPScoreColor((activeTab === "desktop" ? 
                        pageSpeedDesktop?.parsedRawMetrics?.audits?.["largest-contentful-paint"]?.numericValue || 4000 :
                        pageSpeedMobile?.parsedRawMetrics?.audits?.["largest-contentful-paint"]?.numericValue || 4500))}`}
                      style={{ 
                        width: `${Math.min(100, ((activeTab === "desktop" ? 
                          pageSpeedDesktop?.parsedRawMetrics?.audits?.["largest-contentful-paint"]?.numericValue || 4000 :
                          pageSpeedMobile?.parsedRawMetrics?.audits?.["largest-contentful-paint"]?.numericValue || 4500) / 50))}%` 
                      }}
                    ></div>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">Time until largest content element is visible</p>
              </div>
              
              {/* CLS */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <h4 className="text-sm font-medium">Cumulative Layout Shift</h4>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    getCLSRating((activeTab === "desktop" ? 
                      pageSpeedDesktop?.parsedRawMetrics?.audits?.["cumulative-layout-shift"]?.numericValue || 0.15 :
                      pageSpeedMobile?.parsedRawMetrics?.audits?.["cumulative-layout-shift"]?.numericValue || 0.2)) === "Good" 
                      ? "bg-green-100 text-green-800" 
                      : getCLSRating((activeTab === "desktop" ? 
                        pageSpeedDesktop?.parsedRawMetrics?.audits?.["cumulative-layout-shift"]?.numericValue || 0.15 :
                        pageSpeedMobile?.parsedRawMetrics?.audits?.["cumulative-layout-shift"]?.numericValue || 0.2)) === "Needs improvement"
                        ? "bg-yellow-100 text-yellow-800" 
                        : "bg-red-100 text-red-800"
                  }`}>
                    {getCLSRating((activeTab === "desktop" ? 
                      pageSpeedDesktop?.parsedRawMetrics?.audits?.["cumulative-layout-shift"]?.numericValue || 0.15 :
                      pageSpeedMobile?.parsedRawMetrics?.audits?.["cumulative-layout-shift"]?.numericValue || 0.2))}
                  </span>
                </div>
                <p className="text-2xl font-bold mt-2">
                  {formatMetric((activeTab === "desktop" ? 
                    pageSpeedDesktop?.parsedRawMetrics?.audits?.["cumulative-layout-shift"]?.numericValue || 0.15 :
                    pageSpeedMobile?.parsedRawMetrics?.audits?.["cumulative-layout-shift"]?.numericValue || 0.2), null)}
                </p>
                <div className="mt-2">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${getCLSScoreColor((activeTab === "desktop" ? 
                        pageSpeedDesktop?.parsedRawMetrics?.audits?.["cumulative-layout-shift"]?.numericValue || 0.15 :
                        pageSpeedMobile?.parsedRawMetrics?.audits?.["cumulative-layout-shift"]?.numericValue || 0.2))}`}
                      style={{ 
                        width: `${Math.min(100, ((activeTab === "desktop" ? 
                          pageSpeedDesktop?.parsedRawMetrics?.audits?.["cumulative-layout-shift"]?.numericValue || 0.15 :
                          pageSpeedMobile?.parsedRawMetrics?.audits?.["cumulative-layout-shift"]?.numericValue || 0.2) * 300))}%` 
                      }}
                    ></div>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">Measures visual stability of page elements</p>
              </div>
              
              {/* TBT */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <h4 className="text-sm font-medium">Total Blocking Time</h4>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    getTBTRating((activeTab === "desktop" ? 
                      pageSpeedDesktop?.parsedRawMetrics?.audits?.["total-blocking-time"]?.numericValue || 250 :
                      pageSpeedMobile?.parsedRawMetrics?.audits?.["total-blocking-time"]?.numericValue || 350)) === "Good" 
                      ? "bg-green-100 text-green-800" 
                      : getTBTRating((activeTab === "desktop" ? 
                        pageSpeedDesktop?.parsedRawMetrics?.audits?.["total-blocking-time"]?.numericValue || 250 :
                        pageSpeedMobile?.parsedRawMetrics?.audits?.["total-blocking-time"]?.numericValue || 350)) === "Needs improvement"
                        ? "bg-yellow-100 text-yellow-800" 
                        : "bg-red-100 text-red-800"
                  }`}>
                    {getTBTRating((activeTab === "desktop" ? 
                      pageSpeedDesktop?.parsedRawMetrics?.audits?.["total-blocking-time"]?.numericValue || 250 :
                      pageSpeedMobile?.parsedRawMetrics?.audits?.["total-blocking-time"]?.numericValue || 350))}
                  </span>
                </div>
                <p className="text-2xl font-bold mt-2">
                  {formatMetric((activeTab === "desktop" ? 
                    pageSpeedDesktop?.parsedRawMetrics?.audits?.["total-blocking-time"]?.numericValue || 250 :
                    pageSpeedMobile?.parsedRawMetrics?.audits?.["total-blocking-time"]?.numericValue || 350), "ms")}
                </p>
                <div className="mt-2">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${getTBTScoreColor((activeTab === "desktop" ? 
                        pageSpeedDesktop?.parsedRawMetrics?.audits?.["total-blocking-time"]?.numericValue || 250 :
                        pageSpeedMobile?.parsedRawMetrics?.audits?.["total-blocking-time"]?.numericValue || 350))}`}
                      style={{ 
                        width: `${Math.min(100, ((activeTab === "desktop" ? 
                          pageSpeedDesktop?.parsedRawMetrics?.audits?.["total-blocking-time"]?.numericValue || 250 :
                          pageSpeedMobile?.parsedRawMetrics?.audits?.["total-blocking-time"]?.numericValue || 350) / 10))}%` 
                      }}
                    ></div>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">Time main thread is blocked, affecting interactivity</p>
              </div>
            </div>
          </div>
          
          {/* Detailed Metrics - Expandable Section */}
          <div className="p-6 border-b border-gray-200">
            <button 
              className="flex justify-between w-full items-center text-left text-md font-semibold"
              onClick={() => setExpandedMetrics(!expandedMetrics)}
            >
              <span className="flex items-center">
                <Info className="h-5 w-5 mr-2 text-blue-600" />
                Detailed Metrics
              </span>
              <ChevronDown className={`h-5 w-5 transform transition-transform ${expandedMetrics ? 'rotate-180' : ''}`} />
            </button>
            
            {expandedMetrics && (
              <div className="mt-6 space-y-6 animate-fadeIn">
                {/* Key Performance Indicators */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-700 mb-4">Page Performance</h4>
                    <div className="space-y-4">
                      {/* Load Time */}
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm text-gray-600">Total Load Time</span>
                          <span className="font-medium">
                            {formatMetric((activeTab === "desktop" ? 
                              pageSpeedDesktop?.parsedRawMetrics?.load_time || 3747 :
                              pageSpeedMobile?.parsedRawMetrics?.load_time || 4500), "s")}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-500 h-2 rounded-full"
                            style={{ 
                              width: `${Math.min(100, ((activeTab === "desktop" ? 
                                pageSpeedDesktop?.parsedRawMetrics?.load_time || 3747 :
                                pageSpeedMobile?.parsedRawMetrics?.load_time || 4500) / 100))}%` 
                            }}
                          ></div>
                        </div>
                      </div>
                      
                      {/* First Paint */}
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm text-gray-600">First Paint</span>
                          <span className="font-medium">
                            {formatMetric((activeTab === "desktop" ? 
                              pageSpeedDesktop?.parsedRawMetrics?.first_paint || 589 :
                              pageSpeedMobile?.parsedRawMetrics?.first_paint || 789), "ms")}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-green-500 h-2 rounded-full"
                            style={{ 
                              width: `${Math.min(100, ((activeTab === "desktop" ? 
                                pageSpeedDesktop?.parsedRawMetrics?.first_paint || 589 :
                                pageSpeedMobile?.parsedRawMetrics?.first_paint || 789) / 30))}%` 
                            }}
                          ></div>
                        </div>
                      </div>
                      
                      {/* Speed Index */}
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm text-gray-600">Speed Index</span>
                          <span className="font-medium">
                            {formatMetric((activeTab === "desktop" ? 
                              pageSpeedDesktop?.parsedRawMetrics?.audits?.["speed-index"]?.numericValue || 10927 :
                              pageSpeedMobile?.parsedRawMetrics?.audits?.["speed-index"]?.numericValue || 12000), "s")}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={getSpeedIndexColor((activeTab === "desktop" ? 
                              pageSpeedDesktop?.parsedRawMetrics?.audits?.["speed-index"]?.numericValue || 10927 :
                              pageSpeedMobile?.parsedRawMetrics?.audits?.["speed-index"]?.numericValue || 12000))}
                            style={{ 
                              width: `${Math.min(100, ((activeTab === "desktop" ? 
                                pageSpeedDesktop?.parsedRawMetrics?.audits?.["speed-index"]?.numericValue || 10927 :
                                pageSpeedMobile?.parsedRawMetrics?.audits?.["speed-index"]?.numericValue || 12000) / 200))}%` 
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-700 mb-4">Resource Stats</h4>
                    <div className="space-y-4">
                      {/* JS Execution */}
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm text-gray-600">JS Execution Time</span>
                          <span className="font-medium">
                            {formatMetric((activeTab === "desktop" ? 
                              pageSpeedDesktop?.parsedRawMetrics?.audits?.["bootup-time"]?.numericValue || 940 :
                              pageSpeedMobile?.parsedRawMetrics?.audits?.["bootup-time"]?.numericValue || 1200), "ms")}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={getBootupTimeColor((activeTab === "desktop" ? 
                              pageSpeedDesktop?.parsedRawMetrics?.audits?.["bootup-time"]?.numericValue || 940 :
                              pageSpeedMobile?.parsedRawMetrics?.audits?.["bootup-time"]?.numericValue || 1200))}
                            style={{ 
                              width: `${Math.min(100, ((activeTab === "desktop" ? 
                                pageSpeedDesktop?.parsedRawMetrics?.audits?.["bootup-time"]?.numericValue || 940 :
                                pageSpeedMobile?.parsedRawMetrics?.audits?.["bootup-time"]?.numericValue || 1200) / 20))}%` 
                            }}
                          ></div>
                        </div>
                      </div>
                      
                      {/* Requests */}
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm text-gray-600">Total Requests</span>
                          <span className="font-medium">
                            {(activeTab === "desktop" ? 
                              pageSpeedDesktop?.parsedRawMetrics?.requests || 126 :
                              pageSpeedMobile?.parsedRawMetrics?.requests || 140)}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-orange-500 h-2 rounded-full"
                            style={{ 
                              width: `${Math.min(100, ((activeTab === "desktop" ? 
                                pageSpeedDesktop?.parsedRawMetrics?.requests || 126 :
                                pageSpeedMobile?.parsedRawMetrics?.requests || 140) / 2))}%` 
                            }}
                          ></div>
                        </div>
                      </div>
                      
                      {/* Transfer Size */}
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm text-gray-600">Total Transferred</span>
                          <span className="font-medium">
                            {formatBytes((activeTab === "desktop" ? 
                              pageSpeedDesktop?.parsedRawMetrics?.transfer_size || 2985313 :
                              pageSpeedMobile?.parsedRawMetrics?.transfer_size || 3000000))}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-purple-500 h-2 rounded-full"
                            style={{ 
                              width: `${Math.min(100, ((activeTab === "desktop" ? 
                                pageSpeedDesktop?.parsedRawMetrics?.transfer_size || 2985313 :
                                pageSpeedMobile?.parsedRawMetrics?.transfer_size || 3000000) / 60000))}%` 
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Server Response Time */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="text-sm font-semibold text-gray-700">Server Response Time (TTFB)</h4>
                    <span className={`text-xs px-2.5 py-1 rounded-full text-xs font-medium ${getServerResponseClass((activeTab === "desktop" ? 
                      pageSpeedDesktop?.parsedRawMetrics?.audits?.["server-response-time"]?.numericValue || 515 :
                      pageSpeedMobile?.parsedRawMetrics?.audits?.["server-response-time"]?.numericValue || 600))}`}>
                      {getServerResponseText((activeTab === "desktop" ? 
                        pageSpeedDesktop?.parsedRawMetrics?.audits?.["server-response-time"]?.numericValue || 515 :
                        pageSpeedMobile?.parsedRawMetrics?.audits?.["server-response-time"]?.numericValue || 600))}
                    </span>
                  </div>
                  <p className="text-2xl font-bold">
                    {formatMetric((activeTab === "desktop" ? 
                      pageSpeedDesktop?.parsedRawMetrics?.audits?.["server-response-time"]?.numericValue || 515 :
                      pageSpeedMobile?.parsedRawMetrics?.audits?.["server-response-time"]?.numericValue || 600), "ms")}
                  </p>
                  <div className="mt-2">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={getServerResponseColor((activeTab === "desktop" ? 
                          pageSpeedDesktop?.parsedRawMetrics?.audits?.["server-response-time"]?.numericValue || 515 :
                          pageSpeedMobile?.parsedRawMetrics?.audits?.["server-response-time"]?.numericValue || 600))}
                        style={{ 
                          width: `${Math.min(100, ((activeTab === "desktop" ? 
                            pageSpeedDesktop?.parsedRawMetrics?.audits?.["server-response-time"]?.numericValue || 515 :
                            pageSpeedMobile?.parsedRawMetrics?.audits?.["server-response-time"]?.numericValue || 600) / 10))}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Time to first byte (TTFB) measures your server's initial response time</p>
                </div>
              </div>
            )}
          </div>
          
          {/* Opportunities for improvement */}
          <div className="p-6 border-b border-gray-200">
            <button 
              className="flex justify-between w-full items-center text-left text-md font-semibold"
              onClick={() => setExpandedOpportunities(!expandedOpportunities)}
            >
              <span className="flex items-center">
                <Zap className="h-5 w-5 mr-2 text-yellow-500" />
                Opportunities for Improvement
              </span>
              <ChevronDown className={`h-5 w-5 transform transition-transform ${expandedOpportunities ? 'rotate-180' : ''}`} />
            </button>
            
            {expandedOpportunities && (
              <div className="mt-4 space-y-4 animate-fadeIn">
                {/* Render Blocking Resources */}
                {renderOpportunityCard(
                  "Eliminate render-blocking resources",
                  "Resources blocking the first paint of your page. Consider delivering critical JS/CSS inline and deferring all non-critical JS/styles.",
                  (activeTab === "desktop" ? 
                    pageSpeedDesktop?.parsedRawData?.audits?.["render-blocking-insight"]?.score || 0.5 :
                    pageSpeedMobile?.parsedRawData?.audits?.["render-blocking-insight"]?.score || 0.5),
                  "https://web.dev/render-blocking-resources/"
                )}
                
                {/* Unused CSS */}
                {renderOpportunityCard(
                  "Remove unused CSS",
                  "Remove dead rules from stylesheets and defer CSS not used for above-the-fold content.",
                  (activeTab === "desktop" ? 
                    pageSpeedDesktop?.parsedRawData?.audits?.["unused-css-rules"]?.score || 0 :
                    pageSpeedMobile?.parsedRawData?.audits?.["unused-css-rules"]?.score || 0),
                  "https://web.dev/unused-css-rules/"
                )}
                
                {/* Cache TTL */}
                {renderOpportunityCard(
                  "Serve static assets with efficient cache policy",
                  "A long cache lifetime can speed up repeat visits to your page.",
                  (activeTab === "desktop" ? 
                    pageSpeedDesktop?.parsedRawData?.audits?.["uses-long-cache-ttl"]?.score || 0.5 :
                    pageSpeedMobile?.parsedRawData?.audits?.["uses-long-cache-ttl"]?.score || 0.5),
                  "https://web.dev/uses-long-cache-ttl/"
                )}
                
                {/* Image Optimization */}
                {renderOpportunityCard(
                  "Properly size images",
                  "Serve images that are appropriately-sized to save cellular data and improve load time.",
                  (activeTab === "desktop" ? 
                    pageSpeedDesktop?.parsedRawData?.audits?.["uses-responsive-images"]?.score || 1 :
                    pageSpeedMobile?.parsedRawData?.audits?.["uses-responsive-images"]?.score || 1),
                  "https://web.dev/uses-responsive-images/"
                )}
                
                {/* Legacy JavaScript */}
                {renderOpportunityCard(
                  "Avoid legacy JavaScript",
                  "Polyfills and transforms enable legacy browsers to use new JavaScript features, but they're unnecessary for modern browsers.",
                  (activeTab === "desktop" ? 
                    pageSpeedDesktop?.parsedRawData?.audits?.["legacy-javascript"]?.score || 0.5 :
                    pageSpeedMobile?.parsedRawData?.audits?.["legacy-javascript"]?.score || 0.5),
                  "https://web.dev/legacy-javascript/"
                )}
              </div>
            )}
          </div>
          
          {/* Website Screenshots Section */}
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-md font-bold mb-4 flex items-center">
              <Image className="h-5 w-5 mr-2 text-blue-600" />
              Website Screenshot
            </h3>
            
            {/* Screenshot Container */}
            <div className="bg-gray-50 p-4 rounded-lg">
              {(activeTab === "desktop" && 
                (pageSpeedDesktop?.parsedRawData?.fullPageScreenshot?.screenshot?.data || 
                 pageSpeedDesktop?.parsedRawData?.audits?.['full-page-screenshot']?.details?.screenshot?.data ||
                 pageSpeedDesktop?.parsedRawData?.audits?.['final-screenshot']?.details?.data)) ? (
                <div>
                  {/* Enhanced screenshot display with improved zoom capability */}
                  <div className="relative bg-white p-4 border border-gray-200 rounded-lg overflow-hidden">
                    <div className="relative overflow-hidden" style={{ maxHeight: '600px' }}>
                      <img 
                        src={
                          pageSpeedDesktop?.parsedRawData?.fullPageScreenshot?.screenshot?.data || 
                          pageSpeedDesktop?.parsedRawData?.audits?.['full-page-screenshot']?.details?.screenshot?.data ||
                          pageSpeedDesktop?.parsedRawData?.audits?.['final-screenshot']?.details?.data
                        } 
                        alt="Desktop website screenshot" 
                        className="max-w-full mx-auto rounded shadow-sm transition-transform duration-200 hover:scale-105"
                        style={{ cursor: 'zoom-in' }}
                        onClick={(e) => {
                          // Create an interactive modal for viewing the screenshot
                          const modal = document.createElement('div');
                          modal.style.position = 'fixed';
                          modal.style.top = '0';
                          modal.style.left = '0';
                          modal.style.width = '100%';
                          modal.style.height = '100%';
                          modal.style.backgroundColor = 'rgba(0,0,0,0.85)';
                          modal.style.zIndex = '1000';
                          modal.style.display = 'flex';
                          modal.style.alignItems = 'center';
                          modal.style.justifyContent = 'center';
                          modal.style.overflow = 'hidden';
                          modal.style.padding = '20px';
                          
                          // Create a container for the image with proper positioning
                          const imageContainer = document.createElement('div');
                          imageContainer.style.position = 'relative';
                          imageContainer.style.overflow = 'hidden';
                          imageContainer.style.maxWidth = '100%';
                          imageContainer.style.maxHeight = '100%';
                          imageContainer.style.display = 'flex';
                          imageContainer.style.justifyContent = 'center';
                          imageContainer.style.alignItems = 'center';

                          // Create the image element
                          const img = document.createElement('img');
                          img.src = e.target.src;
                          img.style.maxWidth = 'none'; // Allow image to be larger than container
                          img.style.transformOrigin = '0 0';
                          img.style.cursor = 'grab';

                          // Set initial zoom and position
                          let currentZoom = 1;
                          let posX = 0;
                          let posY = 0;
                          let isDragging = false;
                          let startX = 0;
                          let startY = 0;
                          
                          // Function to update image transformation
                          const updateTransform = () => {
                            img.style.transform = `translate(${posX}px, ${posY}px) scale(${currentZoom})`;
                          };
                          
                          // Add zoom controls
                          const zoomControls = document.createElement('div');
                          zoomControls.style.position = 'absolute';
                          zoomControls.style.bottom = '20px';
                          zoomControls.style.left = '50%';
                          zoomControls.style.transform = 'translateX(-50%)';
                          zoomControls.style.display = 'flex';
                          zoomControls.style.gap = '10px';
                          zoomControls.style.backgroundColor = 'rgba(0,0,0,0.5)';
                          zoomControls.style.padding = '8px 12px';
                          zoomControls.style.borderRadius = '20px';
                          zoomControls.style.zIndex = '1010';
                          
                          // Zoom out button
                          const zoomOutBtn = document.createElement('button');
                          zoomOutBtn.innerText = '−';
                          zoomOutBtn.style.width = '30px';
                          zoomOutBtn.style.height = '30px';
                          zoomOutBtn.style.borderRadius = '50%';
                          zoomOutBtn.style.border = 'none';
                          zoomOutBtn.style.backgroundColor = 'white';
                          zoomOutBtn.style.cursor = 'pointer';
                          zoomOutBtn.style.fontSize = '18px';
                          zoomOutBtn.style.display = 'flex';
                          zoomOutBtn.style.alignItems = 'center';
                          zoomOutBtn.style.justifyContent = 'center';
                          zoomOutBtn.addEventListener('click', (e) => {
                            e.stopPropagation();
                            if (currentZoom > 0.5) {
                              currentZoom = Math.max(0.5, currentZoom - 0.5);
                              updateTransform();
                            }
                          });
                          
                          // Zoom display
                          const zoomText = document.createElement('span');
                          zoomText.style.color = 'white';
                          zoomText.style.minWidth = '60px';
                          zoomText.style.textAlign = 'center';
                          zoomText.style.lineHeight = '30px';
                          zoomText.innerText = '100%';
                          
                          // Zoom in button
                          const zoomInBtn = document.createElement('button');
                          zoomInBtn.innerText = '+';
                          zoomInBtn.style.width = '30px';
                          zoomInBtn.style.height = '30px';
                          zoomInBtn.style.borderRadius = '50%';
                          zoomInBtn.style.border = 'none';
                          zoomInBtn.style.backgroundColor = 'white';
                          zoomInBtn.style.cursor = 'pointer';
                          zoomInBtn.style.fontSize = '18px';
                          zoomInBtn.style.display = 'flex';
                          zoomInBtn.style.alignItems = 'center';
                          zoomInBtn.style.justifyContent = 'center';
                          zoomInBtn.addEventListener('click', (e) => {
                            e.stopPropagation();
                            if (currentZoom < 3) {
                              currentZoom = Math.min(3, currentZoom + 0.5);
                              updateTransform();
                            }
                          });
                          
                          // Reset button
                          const resetBtn = document.createElement('button');
                          resetBtn.innerText = '↺';
                          resetBtn.style.width = '30px';
                          resetBtn.style.height = '30px';
                          resetBtn.style.borderRadius = '50%';
                          resetBtn.style.border = 'none';
                          resetBtn.style.backgroundColor = 'white';
                          resetBtn.style.cursor = 'pointer';
                          resetBtn.style.fontSize = '16px';
                          resetBtn.style.display = 'flex';
                          resetBtn.style.alignItems = 'center';
                          resetBtn.style.justifyContent = 'center';
                          resetBtn.style.marginLeft = '10px';
                          resetBtn.addEventListener('click', (e) => {
                            e.stopPropagation();
                            currentZoom = 1;
                            posX = 0;
                            posY = 0;
                            updateTransform();
                            updateZoomText();
                          });
                          
                          // Update zoom text
                          const updateZoomText = () => {
                            zoomText.innerText = `${Math.round(currentZoom * 100)}%`;
                          };
                          
                          // Add close button to the top right
                          const closeBtn = document.createElement('button');
                          closeBtn.innerHTML = '&times;';
                          closeBtn.style.position = 'absolute';
                          closeBtn.style.top = '20px';
                          closeBtn.style.right = '20px';
                          closeBtn.style.width = '40px';
                          closeBtn.style.height = '40px';
                          closeBtn.style.borderRadius = '50%';
                          closeBtn.style.backgroundColor = 'rgba(255,255,255,0.3)';
                          closeBtn.style.border = 'none';
                          closeBtn.style.color = 'white';
                          closeBtn.style.fontSize = '24px';
                          closeBtn.style.cursor = 'pointer';
                          closeBtn.style.display = 'flex';
                          closeBtn.style.alignItems = 'center';
                          closeBtn.style.justifyContent = 'center';
                          closeBtn.style.zIndex = '1010';
                          
                          closeBtn.addEventListener('click', () => {
                            document.body.removeChild(modal);
                          });
                          
                          // Mouse wheel zoom
                          modal.addEventListener('wheel', (e) => {
                            e.preventDefault();
                            const delta = e.deltaY * -0.01;
                            const newZoom = Math.min(Math.max(0.5, currentZoom + delta), 3);
                            
                            // Calculate mouse position relative to image
                            const rect = img.getBoundingClientRect();
                            const mouseX = e.clientX - rect.left;
                            const mouseY = e.clientY - rect.top;
                            
                            // Calculate new position to zoom towards mouse pointer
                            if (newZoom !== currentZoom) {
                              const scale = newZoom / currentZoom;
                              const newPosX = posX - (mouseX * (scale - 1));
                              const newPosY = posY - (mouseY * (scale - 1));
                              
                              currentZoom = newZoom;
                              posX = newPosX;
                              posY = newPosY;
                              
                              updateTransform();
                              updateZoomText();
                            }
                          }, { passive: false });
                          
                          // Mouse drag functionality
                          img.addEventListener('mousedown', (e) => {
                            e.preventDefault();
                            isDragging = true;
                            startX = e.clientX - posX;
                            startY = e.clientY - posY;
                            img.style.cursor = 'grabbing';
                          });
                          
                          modal.addEventListener('mousemove', (e) => {
                            if (isDragging) {
                              posX = e.clientX - startX;
                              posY = e.clientY - startY;
                              updateTransform();
                            }
                          });
                          
                          modal.addEventListener('mouseup', () => {
                            isDragging = false;
                            img.style.cursor = 'grab';
                          });
                          
                          modal.addEventListener('mouseleave', () => {
                            isDragging = false;
                            img.style.cursor = 'grab';
                          });
                          
                          // Double tap/click to reset zoom
                          let lastTap = 0;
                          img.addEventListener('click', (e) => {
                            const currentTime = new Date().getTime();
                            const tapLength = currentTime - lastTap;
                            if (tapLength < 300 && tapLength > 0) {
                              // Double tap/click detected
                              currentZoom = 1;
                              posX = 0;
                              posY = 0;
                              updateTransform();
                              updateZoomText();
                              e.preventDefault();
                            }
                            lastTap = currentTime;
                          });
                          
                          // Touch events for mobile
                          let initialDistance = 0;
                          let initialZoom = 1;
                          
                          img.addEventListener('touchstart', (e) => {
                            if (e.touches.length === 2) {
                              // Two finger gesture
                              initialDistance = Math.hypot(
                                e.touches[0].clientX - e.touches[1].clientX,
                                e.touches[0].clientY - e.touches[1].clientY
                              );
                              initialZoom = currentZoom;
                            } else if (e.touches.length === 1) {
                              // Drag start
                              isDragging = true;
                              startX = e.touches[0].clientX - posX;
                              startY = e.touches[0].clientY - posY;
                            }
                          });
                          
                          img.addEventListener('touchmove', (e) => {
                            e.preventDefault();
                            if (e.touches.length === 2) {
                              // Handle pinch zoom
                              const currentDistance = Math.hypot(
                                e.touches[0].clientX - e.touches[1].clientX,
                                e.touches[0].clientY - e.touches[1].clientY
                              );
                              
                              if (initialDistance > 0) {
                                // Calculate the new zoom level
                                const newZoom = Math.min(Math.max(0.5, initialZoom * (currentDistance / initialDistance)), 3);
                                
                                // Get the midpoint of the two touches
                                const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
                                const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
                                
                                // Convert to image coordinates
                                const rect = img.getBoundingClientRect();
                                const mouseX = midX - rect.left;
                                const mouseY = midY - rect.top;
                                
                                // Calculate the new position
                                const scale = newZoom / currentZoom;
                                posX = posX - (mouseX * (scale - 1));
                                posY = posY - (mouseY * (scale - 1));
                                
                                currentZoom = newZoom;
                                updateTransform();
                                updateZoomText();
                              }
                            } else if (e.touches.length === 1 && isDragging) {
                              // Handle drag
                              posX = e.touches[0].clientX - startX;
                              posY = e.touches[0].clientY - startY;
                              updateTransform();
                            }
                          }, { passive: false });
                          
                          img.addEventListener('touchend', () => {
                            isDragging = false;
                            initialDistance = 0;
                          });
                          
                          // Assemble zoom controls
                          zoomControls.appendChild(zoomOutBtn);
                          zoomControls.appendChild(zoomText);
                          zoomControls.appendChild(zoomInBtn);
                          zoomControls.appendChild(resetBtn);
                          
                          // Append all elements
                          imageContainer.appendChild(img);
                          modal.appendChild(imageContainer);
                          modal.appendChild(closeBtn);
                          modal.appendChild(zoomControls);
                          
                          // Append modal to body
                          document.body.appendChild(modal);
                          
                          // Initialize
                          updateTransform();
                          updateZoomText();
                          
                          // Close on ESC key
                          const handleKeyDown = (e) => {
                            if (e.key === 'Escape') {
                              document.body.removeChild(modal);
                              document.removeEventListener('keydown', handleKeyDown);
                            }
                          };
                          
                          document.addEventListener('keydown', handleKeyDown);
                        }}
                        onError={(e) => {
                          console.error("Error loading screenshot", e);
                          e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YxZjVmOSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTQiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiM2YjcyODAiPkltYWdlIHVuYXZhaWxhYmxlPC90ZXh0Pjwvc3ZnPg==";
                          e.target.classList.add("image-error");
                        }}
                      />
                      {/* Zoom instruction indicator */}
                      <div className="absolute bottom-2 right-2 bg-gray-800 bg-opacity-70 text-white text-xs px-2 py-1 rounded-full flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        Click to zoom
                      </div>
                    </div>
                    <div className="absolute top-2 right-2 bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
                      <Monitor className="h-3 w-3 inline-block mr-1" />
                      Desktop View
                    </div>
                  </div>
                  
                  {/* Screenshot metadata */}
                  <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600 flex items-center">
                        <Calendar className="h-3 w-3 mr-1 text-gray-400" />
                        Captured: {formatDate(pageSpeedDesktop?.created_at)}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600 flex items-center">
                        <Maximize2 className="h-3 w-3 mr-1 text-gray-400" />
                        {pageSpeedDesktop?.parsedRawData?.audits?.['full-page-screenshot']?.details?.screenshot?.width || '1024'}
                        {' × '}
                        {pageSpeedDesktop?.parsedRawData?.audits?.['full-page-screenshot']?.details?.screenshot?.height || '768'}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (activeTab === "mobile" && 
                  (pageSpeedMobile?.parsedRawData?.fullPageScreenshot?.screenshot?.data || 
                   pageSpeedMobile?.parsedRawData?.audits?.['full-page-screenshot']?.details?.screenshot?.data ||
                   pageSpeedMobile?.parsedRawData?.audits?.['final-screenshot']?.details?.data)) ? (
                <div>
                  {/* Enhanced screenshot display with improved zoom capability */}
                  <div className="relative bg-white p-4 border border-gray-200 rounded-lg overflow-hidden">
                    <div className="relative overflow-hidden" style={{ maxHeight: '600px' }}>
                      <img 
                        src={
                          pageSpeedMobile?.parsedRawData?.fullPageScreenshot?.screenshot?.data || 
                          pageSpeedMobile?.parsedRawData?.audits?.['full-page-screenshot']?.details?.screenshot?.data ||
                          pageSpeedMobile?.parsedRawData?.audits?.['final-screenshot']?.details?.data
                        } 
                        alt="Mobile website screenshot" 
                        className="max-w-full max-h-[600px] mx-auto rounded shadow-sm transition-transform duration-200 hover:scale-105"
                        style={{ cursor: 'zoom-in' }}
                        onClick={(e) => {
                          // Create an interactive modal for viewing the screenshot
                          const modal = document.createElement('div');
                          modal.style.position = 'fixed';
                          modal.style.top = '0';
                          modal.style.left = '0';
                          modal.style.width = '100%';
                          modal.style.height = '100%';
                          modal.style.backgroundColor = 'rgba(0,0,0,0.85)';
                          modal.style.zIndex = '1000';
                          modal.style.display = 'flex';
                          modal.style.alignItems = 'center';
                          modal.style.justifyContent = 'center';
                          modal.style.overflow = 'hidden';
                          modal.style.padding = '20px';
                          
                          // Create a container for the image with proper positioning
                          const imageContainer = document.createElement('div');
                          imageContainer.style.position = 'relative';
                          imageContainer.style.overflow = 'hidden';
                          imageContainer.style.maxWidth = '100%';
                          imageContainer.style.maxHeight = '100%';
                          imageContainer.style.display = 'flex';
                          imageContainer.style.justifyContent = 'center';
                          imageContainer.style.alignItems = 'center';

                          // Create the image element
                          const img = document.createElement('img');
                          img.src = e.target.src;
                          img.style.maxWidth = 'none'; // Allow image to be larger than container
                          img.style.transformOrigin = '0 0';
                          img.style.cursor = 'grab';

                          // Set initial zoom and position
                          let currentZoom = 1;
                          let posX = 0;
                          let posY = 0;
                          let isDragging = false;
                          let startX = 0;
                          let startY = 0;
                          
                          // Function to update image transformation
                          const updateTransform = () => {
                            img.style.transform = `translate(${posX}px, ${posY}px) scale(${currentZoom})`;
                          };
                          
                          // Add zoom controls
                          const zoomControls = document.createElement('div');
                          zoomControls.style.position = 'absolute';
                          zoomControls.style.bottom = '20px';
                          zoomControls.style.left = '50%';
                          zoomControls.style.transform = 'translateX(-50%)';
                          zoomControls.style.display = 'flex';
                          zoomControls.style.gap = '10px';
                          zoomControls.style.backgroundColor = 'rgba(0,0,0,0.5)';
                          zoomControls.style.padding = '8px 12px';
                          zoomControls.style.borderRadius = '20px';
                          zoomControls.style.zIndex = '1010';
                          
                          // Zoom out button
                          const zoomOutBtn = document.createElement('button');
                          zoomOutBtn.innerText = '−';
                          zoomOutBtn.style.width = '30px';
                          zoomOutBtn.style.height = '30px';
                          zoomOutBtn.style.borderRadius = '50%';
                          zoomOutBtn.style.border = 'none';
                          zoomOutBtn.style.backgroundColor = 'white';
                          zoomOutBtn.style.cursor = 'pointer';
                          zoomOutBtn.style.fontSize = '18px';
                          zoomOutBtn.style.display = 'flex';
                          zoomOutBtn.style.alignItems = 'center';
                          zoomOutBtn.style.justifyContent = 'center';
                          zoomOutBtn.addEventListener('click', (e) => {
                            e.stopPropagation();
                            if (currentZoom > 0.5) {
                              currentZoom = Math.max(0.5, currentZoom - 0.5);
                              updateTransform();
                            }
                          });
                          
                          // Zoom display
                          const zoomText = document.createElement('span');
                          zoomText.style.color = 'white';
                          zoomText.style.minWidth = '60px';
                          zoomText.style.textAlign = 'center';
                          zoomText.style.lineHeight = '30px';
                          zoomText.innerText = '100%';
                          
                          // Zoom in button
                          const zoomInBtn = document.createElement('button');
                          zoomInBtn.innerText = '+';
                          zoomInBtn.style.width = '30px';
                          zoomInBtn.style.height = '30px';
                          zoomInBtn.style.borderRadius = '50%';
                          zoomInBtn.style.border = 'none';
                          zoomInBtn.style.backgroundColor = 'white';
                          zoomInBtn.style.cursor = 'pointer';
                          zoomInBtn.style.fontSize = '18px';
                          zoomInBtn.style.display = 'flex';
                          zoomInBtn.style.alignItems = 'center';
                          zoomInBtn.style.justifyContent = 'center';
                          zoomInBtn.addEventListener('click', (e) => {
                            e.stopPropagation();
                            if (currentZoom < 3) {
                              currentZoom = Math.min(3, currentZoom + 0.5);
                              updateTransform();
                            }
                          });
                          
                          // Reset button
                          const resetBtn = document.createElement('button');
                          resetBtn.innerText = '↺';
                          resetBtn.style.width = '30px';
                          resetBtn.style.height = '30px';
                          resetBtn.style.borderRadius = '50%';
                          resetBtn.style.border = 'none';
                          resetBtn.style.backgroundColor = 'white';
                          resetBtn.style.cursor = 'pointer';
                          resetBtn.style.fontSize = '16px';
                          resetBtn.style.display = 'flex';
                          resetBtn.style.alignItems = 'center';
                          resetBtn.style.justifyContent = 'center';
                          resetBtn.style.marginLeft = '10px';
                          resetBtn.addEventListener('click', (e) => {
                            e.stopPropagation();
                            currentZoom = 1;
                            posX = 0;
                            posY = 0;
                            updateTransform();
                            updateZoomText();
                          });
                          
                          // Update zoom text
                          const updateZoomText = () => {
                            zoomText.innerText = `${Math.round(currentZoom * 100)}%`;
                          };
                          
                          // Add close button to the top right
                          const closeBtn = document.createElement('button');
                          closeBtn.innerHTML = '&times;';
                          closeBtn.style.position = 'absolute';
                          closeBtn.style.top = '20px';
                          closeBtn.style.right = '20px';
                          closeBtn.style.width = '40px';
                          closeBtn.style.height = '40px';
                          closeBtn.style.borderRadius = '50%';
                          closeBtn.style.backgroundColor = 'rgba(255,255,255,0.3)';
                          closeBtn.style.border = 'none';
                          closeBtn.style.color = 'white';
                          closeBtn.style.fontSize = '24px';
                          closeBtn.style.cursor = 'pointer';
                          closeBtn.style.display = 'flex';
                          closeBtn.style.alignItems = 'center';
                          closeBtn.style.justifyContent = 'center';
                          closeBtn.style.zIndex = '1010';
                          
                          closeBtn.addEventListener('click', () => {
                            document.body.removeChild(modal);
                          });
                          
                          // Mouse wheel zoom
                          modal.addEventListener('wheel', (e) => {
                            e.preventDefault();
                            const delta = e.deltaY * -0.01;
                            const newZoom = Math.min(Math.max(0.5, currentZoom + delta), 3);
                            
                            // Calculate mouse position relative to image
                            const rect = img.getBoundingClientRect();
                            const mouseX = e.clientX - rect.left;
                            const mouseY = e.clientY - rect.top;
                            
                            // Calculate new position to zoom towards mouse pointer
                            if (newZoom !== currentZoom) {
                              const scale = newZoom / currentZoom;
                              const newPosX = posX - (mouseX * (scale - 1));
                              const newPosY = posY - (mouseY * (scale - 1));
                              
                              currentZoom = newZoom;
                              posX = newPosX;
                              posY = newPosY;
                              
                              updateTransform();
                              updateZoomText();
                            }
                          }, { passive: false });
                          
                          // Mouse drag functionality
                          img.addEventListener('mousedown', (e) => {
                            e.preventDefault();
                            isDragging = true;
                            startX = e.clientX - posX;
                            startY = e.clientY - posY;
                            img.style.cursor = 'grabbing';
                          });
                          
                          modal.addEventListener('mousemove', (e) => {
                            if (isDragging) {
                              posX = e.clientX - startX;
                              posY = e.clientY - startY;
                              updateTransform();
                            }
                          });
                          
                          modal.addEventListener('mouseup', () => {
                            isDragging = false;
                            img.style.cursor = 'grab';
                          });
                          
                          modal.addEventListener('mouseleave', () => {
                            isDragging = false;
                            img.style.cursor = 'grab';
                          });
                          
                          // Double tap/click to reset zoom
                          let lastTap = 0;
                          img.addEventListener('click', (e) => {
                            const currentTime = new Date().getTime();
                            const tapLength = currentTime - lastTap;
                            if (tapLength < 300 && tapLength > 0) {
                              // Double tap/click detected
                              currentZoom = 1;
                              posX = 0;
                              posY = 0;
                              updateTransform();
                              updateZoomText();
                              e.preventDefault();
                            }
                            lastTap = currentTime;
                          });
                          
                          // Touch events for mobile
                          let initialDistance = 0;
                          let initialZoom = 1;
                          
                          img.addEventListener('touchstart', (e) => {
                            if (e.touches.length === 2) {
                              // Two finger gesture
                              initialDistance = Math.hypot(
                                e.touches[0].clientX - e.touches[1].clientX,
                                e.touches[0].clientY - e.touches[1].clientY
                              );
                              initialZoom = currentZoom;
                            } else if (e.touches.length === 1) {
                              // Drag start
                              isDragging = true;
                              startX = e.touches[0].clientX - posX;
                              startY = e.touches[0].clientY - posY;
                            }
                          });
                          
                          img.addEventListener('touchmove', (e) => {
                            e.preventDefault();
                            if (e.touches.length === 2) {
                              // Handle pinch zoom
                              const currentDistance = Math.hypot(
                                e.touches[0].clientX - e.touches[1].clientX,
                                e.touches[0].clientY - e.touches[1].clientY
                              );
                              
                              if (initialDistance > 0) {
                                // Calculate the new zoom level
                                const newZoom = Math.min(Math.max(0.5, initialZoom * (currentDistance / initialDistance)), 3);
                                
                                // Get the midpoint of the two touches
                                const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
                                const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
                                
                                // Convert to image coordinates
                                const rect = img.getBoundingClientRect();
                                const mouseX = midX - rect.left;
                                const mouseY = midY - rect.top;
                                
                                // Calculate the new position
                                const scale = newZoom / currentZoom;
                                posX = posX - (mouseX * (scale - 1));
                                posY = posY - (mouseY * (scale - 1));
                                
                                currentZoom = newZoom;
                                updateTransform();
                                updateZoomText();
                              }
                            } else if (e.touches.length === 1 && isDragging) {
                              // Handle drag
                              posX = e.touches[0].clientX - startX;
                              posY = e.touches[0].clientY - startY;
                              updateTransform();
                            }
                          }, { passive: false });
                          
                          img.addEventListener('touchend', () => {
                            isDragging = false;
                            initialDistance = 0;
                          });
                          
                          // Assemble zoom controls
                          zoomControls.appendChild(zoomOutBtn);
                          zoomControls.appendChild(zoomText);
                          zoomControls.appendChild(zoomInBtn);
                          zoomControls.appendChild(resetBtn);
                          
                          // Append all elements
                          imageContainer.appendChild(img);
                          modal.appendChild(imageContainer);
                          modal.appendChild(closeBtn);
                          modal.appendChild(zoomControls);
                          
                          // Append modal to body
                          document.body.appendChild(modal);
                          
                          // Initialize
                          updateTransform();
                          updateZoomText();
                          
                          // Close on ESC key
                          const handleKeyDown = (e) => {
                            if (e.key === 'Escape') {
                              document.body.removeChild(modal);
                              document.removeEventListener('keydown', handleKeyDown);
                            }
                          };
                          
                          document.addEventListener('keydown', handleKeyDown);
                        }}
                        onError={(e) => {
                          console.error("Error loading screenshot", e);
                          e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YxZjVmOSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTQiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiM2YjcyODAiPkltYWdlIHVuYXZhaWxhYmxlPC90ZXh0Pjwvc3ZnPg==";
                          e.target.classList.add("image-error");
                        }}
                      />
                      {/* Zoom instruction indicator */}
                      <div className="absolute bottom-2 right-2 bg-gray-800 bg-opacity-70 text-white text-xs px-2 py-1 rounded-full flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        Click to zoom
                      </div>
                    </div>
                    <div className="absolute top-2 right-2 bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
                      <Smartphone className="h-3 w-3 inline-block mr-1" />
                      Mobile View
                    </div>
                  </div>
                  
                  {/* Screenshot metadata */}
                  <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600 flex items-center">
                        <Calendar className="h-3 w-3 mr-1 text-gray-400" />
                        Captured: {formatDate(pageSpeedMobile?.created_at)}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600 flex items-center">
                        <Maximize2 className="h-3 w-3 mr-1 text-gray-400" />
                        {pageSpeedMobile?.parsedRawData?.audits?.['full-page-screenshot']?.details?.screenshot?.width || '375'}
                        {' × '}
                        {pageSpeedMobile?.parsedRawData?.audits?.['full-page-screenshot']?.details?.screenshot?.height || '667'}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Image className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500 mb-2">No screenshot available for this analysis</p>
                  <p className="text-sm text-gray-400">
                    Run a new analysis to capture a fresh screenshot
                  </p>
                  <div className="mt-4">
                    <button
                      onClick={() => {
                        console.log("Current data:", activeTab === "desktop" ? pageSpeedDesktop : pageSpeedMobile);
                        runPageAnalysis({...website, url: website.root_url});
                      }}
                      disabled={pageSpeedAnalysis.isRunning}
                      className="inline-flex items-center gap-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed"
                    >
                      <RefreshCw className={`h-4 w-4 ${pageSpeedAnalysis.isRunning ? "animate-spin" : ""}`} />
                      {pageSpeedAnalysis.isRunning ? "Running Analysis..." : "Run Analysis"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Website Pages Section */}
      <WebsitePages
        website={website}
        runPageAnalysis={runPageAnalysis}
        pageSpeedAnalysis={pageSpeedAnalysis}
        formatDate={formatDate}
        showToast={showToast}
      />
    </div>
  );
};

export default WebsiteDetailPage;